/** @file Curve of the selected keys: the curve editor's presets; an ease or bezier also shows its numbers. */
import type { Curve } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { NumberField } from '../../ui-kit/NumberField';
import { Select, type SelectOption } from '../../ui-kit/Select';
import { CURVE_PRESETS } from '../curves/curvePresets.data';

type Four = [number, number, number, number];
type Handles = { kind: 'ease' | 'bezier'; numbers: Four };
const MIXED = '';
const CUSTOM = 'custom';
const NAMES: Readonly<Record<Handles['kind'], readonly string[]>> = { ease: ['x1', 'y1', 'x2', 'y2'], bezier: ['x1', 'dv1', 'x2', 'dv2'] };
const curveId = (curve: Curve): string => JSON.stringify(curve);
const handlesOf = (c: Curve | null): Handles | null => (c === null || typeof c === 'string' ? null : 'ease' in c ? { kind: 'ease', numbers: c.ease } : { kind: 'bezier', numbers: c.bezier });

/** The preset `curve` equals, CUSTOM for any other ease or bezier, MIXED when the keys differ (null). */
function choiceOf(curve: Curve | null): string {
  if (curve === null) return MIXED;
  return CURVE_PRESETS.find((p) => curveId(p.curve) === curveId(curve))?.id ?? CUSTOM;
}

/** The presets, after a Mixed entry or before the shown curve when it is no preset (both only shown, never chosen). */
function optionsOf(choice: string, handles: Handles | null, t: Translate): SelectOption[] {
  const presets = CURVE_PRESETS.map((p) => ({ value: p.id, label: t(p.label) }));
  if (choice === MIXED) return [{ value: MIXED, label: t('panels.inspector.curve.mixed'), disabled: true }, ...presets];
  if (choice !== CUSTOM) return presets;
  const label = handles?.kind === 'bezier' ? t('panels.inspector.curve.bezier') : t('panels.inspector.curve.custom');
  return [...presets, { value: CUSTOM, label, disabled: true }];
}

/** A preset commits its curve; the four numbers edit an ease or bezier in place (x handles stay within 0..1). */
export function CurveFields({ curve, onCommit, t }: { curve: Curve | null; onCommit: (c: Curve) => void; t: Translate }) {
  const choice = choiceOf(curve);
  const handles = handlesOf(curve);
  const set = (i: number, v: number): void => {
    if (handles === null) return;
    const next: Four = [...handles.numbers];
    next[i] = v;
    onCommit(handles.kind === 'bezier' ? { bezier: next } : { ease: next });
  };
  const choose = (id: string): void => {
    const preset = CURVE_PRESETS.find((p) => p.id === id);
    if (preset !== undefined) onCommit(preset.curve);
  };
  return (
    <div className="p2-inspector-row">
      <Select label={t('panels.inspector.key.curve')} value={choice} options={optionsOf(choice, handles, t)} onChange={choose} />
      {handles?.numbers.map((n, i) => (
        <NumberField key={i} label={NAMES[handles.kind][i] ?? ''} value={n} step={0.01} precision={3} {...(i % 2 === 0 ? { min: 0, max: 1 } : {})} onCommit={(v) => { set(i, v); }} />
      ))}
    </div>
  );
}
